import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Play, Clock, CheckCircle, Lock, Brain, Code, 
  Database, Cpu, BookOpen, Eye, Rocket, Briefcase,
  ArrowRight, Star, Loader2, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreviewPage = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const response = await axios.get(`${API}/lessons`);
      const sortedLessons = response.data.sort((a, b) => a.order - b.order);
      setLessons(sortedLessons);
      // Set first free lesson as active
      const firstFree = sortedLessons.find(l => l.is_free);
      if (firstFree) setActiveLesson(firstFree);
    } catch (error) {
      console.error('Failed to fetch lessons:', error);
    } finally {
      setLoading(false);
    }
  };

  const freeLessons = lessons.filter(l => l.is_free);
  const lockedLessons = lessons.filter(l => !l.is_free);

  const handlePlayVideo = (lesson) => {
    if (lesson.is_free) {
      setActiveLesson(lesson);
      setIsPlaying(true);
    }
  };

  const getYoutubeEmbedUrl = (youtubeId) => {
    if (!youtubeId) return null;
    return `https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`;
  };

  if (loading) {
    return (
      <div className="page preview-page">
        <Header onEnrollClick={() => window.location.href = '/pricing'} />
        <main className="page-content">
          <div className="loading-state">
            <Loader2 className="spinner" />
            <p>Loading preview content...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page preview-page">
      <Header onEnrollClick={() => window.location.href = '/pricing'} />
      
      <main className="page-content">
        <section className="page-hero preview-hero">
          <div className="container">
            <span className="section-tag">Free Preview</span>
            <h1 className="page-title">Experience the Course</h1>
            <p className="page-subtitle">
              Watch {freeLessons.length} free lessons and see if WSafe AI ML is right for you
            </p>
          </div>
        </section>

        {/* Video Player Section */}
        <section className="video-player-section">
          <div className="container">
            <div className="video-player-grid">
              {/* Main Video */}
              <div className="main-video-container">
                <div className="video-player">
                  {activeLesson?.youtube_id ? (
                    <div className="youtube-embed">
                      <iframe
                        src={getYoutubeEmbedUrl(activeLesson.youtube_id)}
                        title={activeLesson.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ) : isPlaying && activeLesson ? (
                    <div className="video-content">
                      <div className="video-simulation-player">
                        <AlertCircle className="simulation-brain" style={{ color: '#FFB800' }} />
                        <h3>Video Coming Soon</h3>
                        <p className="video-module">{activeLesson.module}</p>
                        <p className="video-desc">
                          This lesson video is being prepared. Check back soon!
                        </p>
                        <Button 
                          className="btn-secondary"
                          onClick={() => setIsPlaying(false)}
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  ) : activeLesson ? (
                    <div className="video-placeholder-large">
                      <div className="video-overlay-large">
                        <button 
                          className="play-button-large"
                          onClick={() => setIsPlaying(true)}
                        >
                          <Play className="play-icon-large" />
                        </button>
                        <h3>{activeLesson.title}</h3>
                        <p>{activeLesson.module}</p>
                        <span className="video-duration-badge">
                          <Clock size={14} /> {activeLesson.duration}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="video-placeholder-large">
                      <div className="video-overlay-large">
                        <Brain className="play-icon-large" style={{ marginLeft: 0 }} />
                        <h3>Select a Lesson</h3>
                        <p>Choose a lesson from the playlist to start watching</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {activeLesson && (
                  <div className="video-info">
                    <h2>{activeLesson.title}</h2>
                    <p>{activeLesson.description}</p>
                    <div className="video-meta-info">
                      <span><Clock size={16} /> {activeLesson.duration}</span>
                      <span><BookOpen size={16} /> {activeLesson.module}</span>
                      {activeLesson.youtube_id && (
                        <span className="video-available-badge">
                          <CheckCircle size={16} /> Video Available
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Playlist */}
              <div className="video-playlist">
                <h3 className="playlist-title">
                  <Star className="star-icon" /> Free Lessons ({freeLessons.length})
                </h3>
                <div className="playlist-items">
                  {freeLessons.map((lesson) => (
                    <div 
                      key={lesson.id}
                      className={`playlist-item ${activeLesson?.id === lesson.id ? 'active' : ''} ${lesson.youtube_id ? 'has-video' : ''}`}
                      onClick={() => handlePlayVideo(lesson)}
                    >
                      <div className="playlist-item-icon">
                        {lesson.youtube_id ? (
                          <Play size={16} />
                        ) : (
                          <Clock size={16} />
                        )}
                      </div>
                      <div className="playlist-item-info">
                        <span className="playlist-module">{lesson.module}</span>
                        <h4>{lesson.title}</h4>
                        <span className="playlist-duration">
                          <Clock size={12} /> {lesson.duration}
                          {lesson.youtube_id && (
                            <span className="video-ready"> • Ready</span>
                          )}
                        </span>
                      </div>
                      {activeLesson?.id === lesson.id && (
                        <CheckCircle className="playlist-check" />
                      )}
                    </div>
                  ))}
                </div>

                <h3 className="playlist-title locked-title">
                  <Lock size={16} /> Premium Lessons ({lockedLessons.length})
                </h3>
                <div className="playlist-items locked">
                  {lockedLessons.map((lesson) => (
                    <div key={lesson.id} className="playlist-item locked">
                      <div className="playlist-item-icon locked">
                        <Lock size={16} />
                      </div>
                      <div className="playlist-item-info">
                        <span className="playlist-module">{lesson.module}</span>
                        <h4>{lesson.title}</h4>
                        <span className="playlist-duration">
                          <Clock size={12} /> {lesson.duration}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <Link to="/pricing" className="unlock-link">
                  <Button className="btn-primary unlock-btn">
                    Unlock All 120+ Hours <ArrowRight size={16} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* What You'll Learn */}
        <section className="preview-features">
          <div className="container">
            <h2 className="section-title">What's in the Full Course?</h2>
            <div className="features-grid">
              <div className="feature-card">
                <Brain className="feature-icon" />
                <h4>Deep Learning</h4>
                <p>Master neural networks, CNNs, RNNs, and transformers</p>
              </div>
              <div className="feature-card">
                <Code className="feature-icon" />
                <h4>Hands-On Projects</h4>
                <p>Build 25+ real-world projects with industry datasets</p>
              </div>
              <div className="feature-card">
                <Database className="feature-icon" />
                <h4>Data Science</h4>
                <p>Learn data preprocessing, visualization, and analysis</p>
              </div>
              <div className="feature-card">
                <Cpu className="feature-icon" />
                <h4>MLOps</h4>
                <p>Deploy models to production with modern tools</p>
              </div>
              <div className="feature-card">
                <Eye className="feature-icon" />
                <h4>Computer Vision</h4>
                <p>Image classification, object detection, and more</p>
              </div>
              <div className="feature-card">
                <Rocket className="feature-icon" />
                <h4>Career Support</h4>
                <p>Resume review, interview prep, and job placement</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="preview-cta">
          <div className="container">
            <div className="cta-card">
              <h2>Ready to Master AI & Machine Learning?</h2>
              <p>Join 12,500+ students transforming their careers</p>
              <div className="cta-buttons">
                <Link to="/pricing">
                  <Button className="btn-primary">View Pricing</Button>
                </Link>
                <Link to="/syllabus">
                  <Button className="btn-secondary">See Full Syllabus</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PreviewPage;
